import { useCallback, useEffect, useState } from "react";

import { USER_ROLE_PRODUCER } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { AipPassApiHelper, UsersApiHelper } from "api/ApiHelper";
import { fetchAuthSession } from "aws-amplify/auth";
import { USER_ROLE_ADMIN, USER_ROLE_AGENT } from "core/useSession";

/**
 * Hook for fetching and managing user data
 */
export const useUser = ({ user_role, producer_token, agent_token, globalDispatch }, handleError) => {
  const [myUser, setMyUser] = useState();
  const [activeProducer, setActiveProducer] = useState();
  const [activeAgent, setActiveAgent] = useState();
  const [userGrants, setUserGrants] = useState(null);

  // Load current user info
  useEffect(() => {
    const loadMyUser = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken;
        setMyUser({
          name: idToken?.payload?.["cognito:username"],
        });
      } catch (err) {
        handleError(err);
      }
    };

    if (user_role) {
      loadMyUser();
    }
  }, [user_role, handleError]);

  // Load active producer info
  useEffect(() => {
    const selectActiveProducer = async () => {
      try {
        let _producer = await UsersApiHelper.users_selectObject(
          "Member",
          { producer_token },
          { user_role, producer_token, agent_token }
        );
        if (_producer?.length) {
          setActiveProducer(_producer[0]);
        } else {
          setActiveProducer(null);
        }
      } catch (err) {
        handleError(err);
        setActiveProducer(null);
      }
    };

    if (producer_token) {
      selectActiveProducer();
    } else {
      setActiveProducer(null);
    }
  }, [producer_token, user_role, agent_token, handleError]);

  // Load active agent info
  useEffect(() => {
    const selectActiveAgent = async () => {
      try {
        let _agent = await UsersApiHelper.users_selectObject("Member", { agent_token }, { agent_token });
        if (_agent?.length) {
          setActiveAgent(_agent[0]);
        } else {
          setActiveAgent(null);
        }
      } catch (err) {
        handleError(err);
        setActiveAgent(null);
      }
    };

    if (user_role === USER_ROLE_AGENT && agent_token) {
      selectActiveAgent();
    } else {
      setActiveAgent(null);
    }
  }, [user_role, agent_token, handleError]);

  // Load user grants
  useEffect(() => {
    const loadUserGrants = async () => {
      try {
        const _userGrants = await UsersApiHelper.selectUserGrantItems(
          { user_role, agent_token, producer_token, is_active: true },
          { user_role, producer_token, agent_token }
        );
        setUserGrants(_userGrants);
      } catch (err) {
        handleError(err);
        setUserGrants([]);
      }
    };

    if (user_role === USER_ROLE_AGENT && producer_token) {
      loadUserGrants();
    } else {
      setUserGrants([]);
    }
  }, [user_role, producer_token, agent_token, handleError]);

  const handleLogoutClick = useCallback(() => {
    globalDispatch({ type: "logout" });
  }, [globalDispatch]);

  const calculateIsModuleAllowed = useCallback(
    (_module) => {
      if (!userGrants?.length) {
        return Boolean(producer_token && (user_role === USER_ROLE_PRODUCER || user_role === USER_ROLE_ADMIN));
      } else {
        const _userGrant = userGrants?.find((ug) => ug?.module === _module);
        if (!_userGrant) {
          return userGrants?.[0]?.is_master_read || userGrants?.[0]?.is_master_write;
        } else {
          return (
            _userGrant?.is_master_read || _userGrant?.is_master_write || _userGrant?.is_read || _userGrant?.is_write
          );
        }
      }
    },
    [userGrants, producer_token, user_role]
  );

  return {
    myUser,
    activeProducer,
    activeAgent,
    userGrants,
    calculateIsModuleAllowed,
    handleLogoutClick,
  };
};
